import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getProjectFiles, deleteFile as deleteFileApi, PMFile } from '../services/fileApi';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

interface PMFileStoreState {
  // 資料狀態
  files: PMFile[];
  filesByProject: Record<string, PMFile[]>; // 按專案 ID 快取
  isLoading: boolean;
  error: string | null;

  // 刪除狀態
  deletingFileId: string | null;

  // Actions
  fetchFiles: (projectId: string) => Promise<void>;
  deleteFile: (fileId: string) => Promise<void>;
}

export const usePMFileStore = create<PMFileStoreState>()(
  persist(
    (set, get) => ({
      // 初始狀態
      files: [],
      filesByProject: {},
      isLoading: false,
      error: null,
      deletingFileId: null,

      // 取得專案檔案列表（帶快取）
      fetchFiles: async (projectId: string) => {
        const state = get();

        // 有快取先顯示、背景重抓（stale-while-revalidate，R31）
        const cached = state.filesByProject[projectId];
        if (cached) {
          set({ files: cached, isLoading: false, error: null });
        } else {
          // 沒有快取：先清掉上一個專案的檔案，避免換專案時閃一下別的專案的清單
          set({ files: [], isLoading: true, error: null });
        }

        try {
          const data = await getProjectFiles(projectId);

          set((state) => ({
            files: data,
            filesByProject: {
              ...state.filesByProject,
              [projectId]: data
            },
            isLoading: false,
            error: null
          }));
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || i18n.t('pages:pm.files.fetchFailed');
          set({
            isLoading: false,
            error: errorMessage
          });
          toast.error(errorMessage);
        }
      },

      // 刪除檔案
      deleteFile: async (fileId: string) => {
        set({ deletingFileId: fileId });

        try {
          await deleteFileApi(fileId);

          // 從所有快取中移除該檔案
          set((state) => {
            const newFilesByProject = { ...state.filesByProject };

            // 從每個專案的快取中移除
            Object.keys(newFilesByProject).forEach(projectId => {
              newFilesByProject[projectId] = newFilesByProject[projectId].filter(
                file => file.id !== fileId
              );
            });

            return {
              files: state.files.filter(file => file.id !== fileId),
              filesByProject: newFilesByProject,
              deletingFileId: null
            };
          });

          toast.success(i18n.t('pages:pm.files.deleteSuccess'));
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || i18n.t('pages:pm.files.deleteFailed');
          set({ deletingFileId: null });
          toast.error(errorMessage);
          throw error;
        }
      },
    }),
    {
      name: 'pm-file-storage',
      partialize: (state) => ({
        filesByProject: state.filesByProject, // 只持久化快取資料
      }),
    }
  )
);
